import { Estimate, EstimateStatus } from '../types';

/**
 * Qué estimados NO se borran, y por qué.
 *
 * Un borrador es un cálculo: sobra, se tira. Un estimado aceptado es OTRA cosa
 * —es lo que el cliente dijo que sí—, y de él cuelga trabajo real: el proyecto
 * que se creó al ganarlo, las horas que se le apuntaron, y en 27 casos de
 * producción una firma con su contrato archivado. Borrarlo no deja un hueco:
 * deja un proyecto que ya no sabe cuánto se acordó cobrar.
 *
 * Ya pasó. Al escribir esto había en producción un proyecto apuntando a un
 * estimado que ya no existe.
 *
 * El camino de salida NO es borrar: es Archivar (`EstimateStatus.ARCHIVED`),
 * que saca el estimado de la lista activa y conserva el documento.
 */

/** Por qué no se puede borrar. La interfaz elige el texto con esto. */
export type DeletionBlock = 'ganado' | 'firmado' | 'convertido';

/**
 * `null` = se puede borrar. Si no, el motivo del bloqueo.
 *
 * El orden importa: se devuelve la razón MÁS fuerte, que es la que hay que
 * enseñarle a quien lo intenta. Un estimado firmado y además convertido se
 * explica por la firma, no por el proyecto.
 */
export function deletionBlock(
    estimate: Pick<Estimate, 'status' | 'signature' | 'signedDate' | 'executedContractPath' | 'convertedToProjectId'>,
): DeletionBlock | null {
    // La firma es evidencia: el contrato ejecutado lo sella el servidor y el
    // cliente ni siquiera puede escribir esos campos (ver estimateAllowedKeys
    // en services/database.ts). Borrar la fila tiraría lo único que dice qué
    // se firmó.
    if (estimate.signature || estimate.signedDate || estimate.executedContractPath) return 'firmado';

    if (PROTEGIDOS_POR_ESTADO.has(estimate.status)) return 'ganado';

    // Sin estado ganado pero con proyecto: pasa cuando alguien devuelve el
    // estado a mano después de haber convertido. El proyecto sigue vivo y
    // sigue necesitando su origen.
    if (estimate.convertedToProjectId) return 'convertido';

    return null;
}

/**
 * Estados en los que el estimado dejó de ser un cálculo.
 *
 * COMPLETED y PAID entran igual que ACCEPTED: son pasos POSTERIORES a ganar
 * —el trabajo se hizo, el trabajo se cobró—, así que si Aceptado se protege,
 * estos con más razón. ARCHIVED no entra: archivar es justamente la salida que
 * se ofrece en vez de borrar, y si archivar dejara la fila igual de indeleble,
 * no serviría de nada como alternativa.
 *
 * OJO, Y ES A PROPÓSITO: archivar un Aceptado lo vuelve borrable. Eso NO abre
 * la puerta a lo que este archivo evita, porque los dos motivos que de verdad
 * pesan —la firma y el proyecto— se leen de campos que no cambian al mover el
 * estado, y siguen bloqueando estando archivado. Lo único que se puede borrar
 * tras archivar es un Aceptado sin firma y sin proyecto: una fila de la que no
 * cuelga nada, y solo después de que alguien la sacara a mano del embudo de
 * ganados. Es una decisión deliberada, no un descuido.
 */
const PROTEGIDOS_POR_ESTADO = new Set<EstimateStatus>([
    EstimateStatus.ACCEPTED,
    EstimateStatus.COMPLETED,
    EstimateStatus.PAID,
]);

/**
 * Motivo → clave del texto que se le enseña a quien intentó borrar.
 *
 * Vive aquí y no en cada pantalla para que el candado y la explicación no se
 * separen: si mañana se añade un motivo, TypeScript obliga a escribir su texto.
 */
export const MENSAJE_BLOQUEO = {
    ganado: 'estimados.borrar.bloqueado.ganado',
    firmado: 'estimados.borrar.bloqueado.firmado',
    convertido: 'estimados.borrar.bloqueado.convertido',
} as const satisfies Record<DeletionBlock, string>;

/** Atajo legible para los sitios que solo preguntan sí o no. */
export function canDeleteEstimate(
    estimate: Pick<Estimate, 'status' | 'signature' | 'signedDate' | 'executedContractPath' | 'convertedToProjectId'>,
): boolean {
    return deletionBlock(estimate) === null;
}

/** Separa una selección en lo que se puede borrar y lo que no. Para el borrado múltiple. */
export function splitDeletable<T extends Pick<Estimate, 'status' | 'signature' | 'signedDate' | 'executedContractPath' | 'convertedToProjectId'>>(
    estimates: T[],
): { borrables: T[]; protegidos: T[] } {
    const borrables: T[] = [];
    const protegidos: T[] = [];
    for (const e of estimates) {
        (canDeleteEstimate(e) ? borrables : protegidos).push(e);
    }
    return { borrables, protegidos };
}
